import { ChevronDown, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '../../../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu'
import { InfluencerTableFilters } from '../../../../core/interface/chronique.interface'
import { Badge } from '../../../ui/badge'

import SelectionIcon from '../../../../assets/icons/table-filters/selection.svg'
import StatusIcon from '../../../../assets/icons/table-filters/status.svg'
import AlreadyInfluencedIcon from '../../../../assets/icons/table-filters/already_influenced.svg'
import ChroniqueIcon from '../../../../assets/icons/table-filters/chronique.svg'
import CompleteIcon from '../../../../assets/icons/table-filters/complete.svg'
import PromiseIcon from '../../../../assets/icons/table-filters/promise.svg'

interface Props {
  filters: InfluencerTableFilters
  onFiltersChange: (filters: InfluencerTableFilters) => void
  filterData: InfluencerTableFilters
}

const mappingIconFilterLabels = {
  selection: SelectionIcon,
  status: StatusIcon,
  alreadyInfluenced: AlreadyInfluencedIcon,
  chronique: ChroniqueIcon,
  complete: CompleteIcon,
  promise: PromiseIcon,
}

const filterOrder: (keyof InfluencerTableFilters)[] = [
  'selection',
  'status',
  'chronique',
  'alreadyInfluenced',
  'complete',
  'promise',
]

const InfluencerTableHeader = ({
  filters,
  onFiltersChange,
  filterData,
}: Props) => {
  const { t } = useTranslation()

  const filterLabels = {
    selection: t('chroniqueurView.filterLabels.selection'),
    status: t('chroniqueurView.filterLabels.status'),
    alreadyInfluenced: t('chroniqueurView.filterLabels.alreadyInfluenced'),
    chronique: t('chroniqueurView.filterLabels.chronique'),
    complete: t('chroniqueurView.filterLabels.complete'),
    promise: t('chroniqueurView.filterLabels.promise'),
  }

  const handleFilterChange = (
    filterType: keyof Props['filters'],
    value: string,
    checked: boolean,
  ) => {
    const currentValues = (filters[filterType] as any[]) || []
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter((v: any) => v !== value)

    onFiltersChange({
      ...filters,
      [filterType]: newValues,
    })
  }

  const getActiveCount = (filterType: keyof Props['filters']) => {
    return filters[filterType]?.length || 0
  }

  const hasActiveFilters = (filterType: keyof Props['filters']) => {
    return (filters[filterType]?.length || 0) > 0
  }

  return (
    <div className=" bg-[#3B3B3B]">
      {/* Filtres Actifs */}
      {Object.values(filters).some(
        (values: string[]) => values && values.length > 0,
      ) && (
        <div className="px-6 py-2 flex items-center justify-center flex-wrap gap-2 ">
          {filterOrder.map((filterType) => {
            const values = filters[filterType]
            if (!values || values.length === 0) return null

            return values.map((value: string) => (
              <Badge
                key={`${filterType}-${value}`}
                className="bg-[#595959] border border-[#B2B2B2] hover:bg-[#595959] flex items-center gap-1 p-1"
              >
                <img
                  src={
                    mappingIconFilterLabels[
                      filterType as keyof typeof mappingIconFilterLabels
                    ]
                  }
                  alt=""
                  className="h-3 w-3 mr-1"
                />
                <span>
                  {t(`chroniqueurView.${String(filterType)}.${value}`)}
                </span>
                <Button
                  onClick={() => handleFilterChange(filterType, value, false)}
                  size="sm"
                  variant="ghost"
                  className="h-4 w-4 p-0 "
                >
                  <X className="h-4 w-4" />
                </Button>
              </Badge>
            ))
          })}
        </div>
      )}

      {/* Barre de filtres */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-center gap-4 flex-wrap">
          {filterOrder
            .filter(
              (filterType) =>
                filters[filterType] !== undefined &&
                filterData[filterType] !== undefined,
            )
            .map((filterType) => {
              const options = filterData[filterType]

              return (
                <DropdownMenu key={filterType}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className={`h-8 text-xs font-medium ${
                        hasActiveFilters(filterType)
                          ? 'text-white'
                          : 'text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      <img
                        src={mappingIconFilterLabels[filterType]}
                        alt=""
                        className="h-3 w-3 mr-1"
                      />
                      {filterLabels[filterType]}
                      {hasActiveFilters(filterType) && (
                        <span className="ml-1 px-1 bg-blue-500 text-white rounded text-xs">
                          {getActiveCount(filterType)}
                        </span>
                      )}
                      <ChevronDown className="h-3 w-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="w-56 bg-white border shadow-lg z-[205]"
                  >
                    {options?.map((option: any) => (
                      <DropdownMenuCheckboxItem
                        key={option}
                        checked={
                          (filters[filterType] as any[])?.includes(option) ||
                          false
                        }
                        onCheckedChange={(checked) =>
                          handleFilterChange(filterType, option, !!checked)
                        }
                        className="text-sm"
                      >
                        {t(`chroniqueurView.${String(filterType)}.${option}`)}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )
            })}
        </div>
      </div>
    </div>
  )
}

export default InfluencerTableHeader
